import { onMount, onCleanup, Show, createSignal } from 'solid-js';
import { render } from 'solid-js/web';
import wm from './lib/wmStore.js';
import toast from './lib/toastStore.js';
import Desktop from './components/Desktop.jsx';
import StandaloneWidgetView, { getStandaloneWidgetType } from './components/StandaloneWidgetView.jsx';
import Taskbar from './components/Taskbar.jsx';
import HomeMenu from './components/HomeMenu.jsx';
import HamburgerTrigger from './components/HamburgerTrigger.jsx';
import Hotkeys from './components/Hotkeys.jsx';
import Toasts from './components/Toasts.jsx';
import SpyBar from './components/SpyBar.jsx';
import ItemDetailModal from './widgets/ItemDetailModal.jsx';
import UserDetailModal from './widgets/UserDetailModal.jsx';
import GuildDetailModal from './widgets/GuildDetailModal.jsx';
import SyncSessionModal from './widgets/SyncSessionModal.jsx';
import ImageLightboxModal from './components/ImageLightboxModal.jsx';
import HoverCard from './components/HoverCard.jsx';
import Hints from './components/Hints.jsx';
import FissalOnboarding from './components/FissalOnboarding.jsx';
import FissalGreeter from './components/FissalGreeter.jsx';
import FissalBubble from './components/FissalBubble.jsx';
import ThemedCursor from './components/ThemedCursor.jsx';
import './components/SpyBar.css';
import { loadTheme } from './components/themeSwitcher.helper.js';
import { parseDeepLink, dispatchDeepLink } from './lib/deepLink.js';

import './styles/tokens.css';
import './styles/app.css';
import './styles/themeBackgrounds.css';

loadTheme();

function App() {
  const [standaloneWidget, setStandaloneWidget] = createSignal(getStandaloneWidgetType());

  function syncStandaloneFromUrl() {
    setStandaloneWidget(getStandaloneWidgetType());
  }

  onMount(() => {
    window.addEventListener('hashchange', syncStandaloneFromUrl);
    window.addEventListener('popstate', syncStandaloneFromUrl);
    wm.init();
    // Surface OAuth / auth-callback errors that the server redirects us
    // back with as ?error=…  Without this the user sees nothing on a
    // failed Discord login (especially on mobile Safari).
    try {
      const params = new URLSearchParams(window.location.search);
      const err = params.get('error');
      if (err) {
        const map = {
          auth_failed:       { msg: 'Discord login failed', detail: 'The OAuth round-trip did not complete. Please try again — on mobile, ensure cookies are allowed for this site.' },
          no_code:           { msg: 'Discord login cancelled', detail: 'No authorization code was returned. If this was unexpected, try logging in again.' },
          token_failed:      { msg: 'Discord token exchange failed', detail: 'Could not exchange the auth code for a token. Try again or contact an admin.' },
          user_fetch_failed: { msg: 'Could not fetch your Discord profile', detail: 'Discord returned an empty user record. Please retry.' },
        };
        const m = map[err] || { msg: `Login error: ${err}` };
        toast.error(m.msg, { detail: m.detail, ttl: 9000 });
        // Strip the query string so a refresh does not re-toast.
        params.delete('error');
        const qs = params.toString();
        const url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
        window.history.replaceState({}, '', url);
      }
    } catch { /* ignore */ }

    // Issue #307 — Discord deep-links (/price?item=…, /sales?tag=…)
    // arrive as bare paths the SPA fallback rewrites to index.html.
    // Translate the URL into a modal action once the WM has booted
    // (the modal listeners only mount after wm.state.loaded === true,
    // since they live inside the <Show when={wm.state.loaded}> below).
    try {
      const link = parseDeepLink(window.location.pathname, window.location.search);
      if (link) {
        const fire = () => {
          if (dispatchDeepLink(link)) {
            // Replace the URL with the SPA root so a refresh / share
            // doesn't re-trigger and so the user lands on a stable URL.
            try { window.history.replaceState({}, '', '/v2/'); } catch { /* ignore */ }
          }
        };
        const tryFire = (attempt = 0) => {
          if (wm.state.loaded) {
            // Defer one tick so the <Show>'s children (the modals)
            // have a chance to register their onMount listeners.
            setTimeout(fire, 0);
          } else if (attempt < 100) {
            setTimeout(() => tryFire(attempt + 1), 50);
          }
        };
        tryFire();
      }
    } catch { /* ignore — deep-link is best-effort */ }
  });
  onCleanup(() => {
    window.removeEventListener('hashchange', syncStandaloneFromUrl);
    window.removeEventListener('popstate', syncStandaloneFromUrl);
  });

  function exitStandalone() {
    try {
      window.location.hash = '';
      history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch { /* ignore */ }
    setStandaloneWidget(null);
  }

  return (
    <>
      <Show when={!standaloneWidget()}>
        <SpyBar />
      </Show>
      <Show when={wm.state.loaded} fallback={<div style={{ padding: '40px', color: 'var(--fissal)', 'font-family': 'var(--font-terminal)', 'font-size': '24px' }}>◐ Booting Redfur Terminal…</div>}>
        <Show when={standaloneWidget()}>
          <StandaloneWidgetView widgetType={standaloneWidget()} onExit={exitStandalone} />
        </Show>
        <ImageLightboxModal />
        <Show when={!standaloneWidget()}>
          <Desktop />
          <HomeMenu />
          <HamburgerTrigger />
          <Hotkeys />
          <Toasts />
          <Taskbar />
          <ItemDetailModal />
          <UserDetailModal />
          <GuildDetailModal />
          <SyncSessionModal />
          <HoverCard />
          <Hints />
          <FissalOnboarding />
          <FissalGreeter />
          <FissalBubble />
          <ThemedCursor />
        </Show>
      </Show>
    </>
  );
}

render(() => <App />, document.getElementById('root'));
